import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, Sparkles, Building2, ReceiptText, Ticket,
  Users, Calculator, Plug, BookOpen, ChevronDown, Globe, HeartHandshake,
} from 'lucide-react';
import { HRPromoStrip } from './HRPromoBanner';

const PRODUCTS = [
  { label: 'StayMii Lite\u2122', desc: 'Cloud PMS for hotels & homestays', href: '/property-management-software-malaysia', icon: Building2 },
  { label: 'StayMii e-Invoice\u2122', desc: 'Automated LHDN e-Invoicing', href: '/einvoice/product', icon: ReceiptText },
  { label: 'StayMii e-Ticket\u2122', desc: 'Attraction & event ticketing', href: '/e-ticket', icon: Ticket },
  { label: 'StayMii HR\u2122', desc: 'Staff onboarding & HR', href: '/staymii-hr', icon: Users, badge: 'FREE' },
  { label: 'StayMii Care\u2122', desc: 'Nursing home resident & billing system', href: '/staymii-care', icon: HeartHandshake },
];

const RESOURCES = [
  { label: 'Global Market Connect', desc: 'Distribute tickets to global marketplaces', href: '/global-market-connect', icon: Globe, tag: 'New' },
  { label: 'Integrations', desc: 'Beds24, PMS & channel managers', href: '/partners', icon: Plug },
  { label: 'Free Invoice Tool', desc: 'Generate an invoice in seconds', href: '/free-invoice-generator', icon: Calculator, tag: 'Free' },
  { label: 'Features', desc: 'Everything StayMii can do', href: '/features', icon: Sparkles },
  { label: 'Blog & Guides', desc: 'e-Invoice tips & product news', href: '/blog', icon: BookOpen },
  { label: 'Beds24 Knowledge Centre', desc: 'Guides, API & Malaysia e-Invoice', href: '/beds24', icon: BookOpen, tag: 'New' },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const MenuItemRow = ({ item, onClick, testPrefix }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-white/5"
      data-testid={`${testPrefix}-${slug(item.label)}`}
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-400 transition-colors group-hover:bg-blue-500/20">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
          {item.label}
          {item.badge && (
            <span className="rounded bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold leading-none text-[#0B1120]">
              {item.badge}
            </span>
          )}
          {item.tag && (
            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-white/60">
              {item.tag}
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-xs text-white/45">{item.desc}</span>
      </span>
    </Link>
  );
};

const LandingNavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'products' | 'resources' | null (desktop)
  const [mobileSection, setMobileSection] = useState(null); // 'products' | 'resources' | null
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMenu(null);
    setMobileSection(null);
  }, [location.pathname]);

  // Close desktop dropdowns on outside click / Escape
  useEffect(() => {
    const onDown = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');
  const groupActive = (items) => items.some((i) => isActive(i.href));

  const DesktopDropdown = ({ id, label, items }) => {
    const open = openMenu === id;
    return (
      <div
        className="relative"
        onMouseEnter={() => setOpenMenu(id)}
        onMouseLeave={() => setOpenMenu(null)}
      >
        <button
          type="button"
          onClick={() => setOpenMenu(open ? null : id)}
          className={`flex items-center gap-1 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
            open || groupActive(items) ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
          }`}
          data-testid={`nav-${id}-trigger`}
          aria-expanded={open}
        >
          {label}
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div
            className="absolute left-0 top-full pt-2"
            data-testid={`nav-${id}-panel`}
          >
            <div className="w-[340px] rounded-2xl border border-white/10 bg-[#111827] p-2 shadow-2xl shadow-black/40">
              {items.map((item) => (
                <MenuItemRow key={item.href} item={item} testPrefix={`nav-${id}`} onClick={() => setOpenMenu(null)} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const MobileAccordion = ({ id, label, items }) => {
    const open = mobileSection === id;
    return (
      <div className="rounded-xl bg-white/[0.03]">
        <button
          type="button"
          onClick={() => setMobileSection(open ? null : id)}
          className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold text-white"
          data-testid={`mobile-${id}-toggle`}
          aria-expanded={open}
        >
          {label}
          <ChevronDown className={`h-4 w-4 text-white/60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="space-y-0.5 px-1 pb-2">
            {items.map((item) => (
              <MenuItemRow key={item.href} item={item} testPrefix={`mobile-${id}`} onClick={() => setMobileMenuOpen(false)} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <HRPromoStrip />
      <nav
        ref={navRef}
        className="fixed left-0 right-0 top-0 z-50 bg-[#0F172A]/95 backdrop-blur-md border-b border-white/5"
        data-testid="landing-navbar"
        style={{ marginTop: 'var(--hr-promo-offset, 0px)' }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between lg:h-20">

            {/* Logo */}
            <Link to="/" className="flex shrink-0 items-center" data-testid="nav-logo">
              <img
                src="/staymii-logo-horizontal-white.png"
                alt="StayMii"
                className="h-20 w-auto lg:h-24"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden items-center gap-1 lg:flex">
              <DesktopDropdown id="products" label="Products" items={PRODUCTS} />
              <Link
                to="/pricing"
                className={`rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive('/pricing') ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                data-testid="nav-item-pricing"
              >
                Pricing
              </Link>
              <DesktopDropdown id="resources" label="Resources" items={RESOURCES} />
              <Link
                to="/contact"
                className={`rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive('/contact') ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                data-testid="nav-item-contact"
              >
                Contact
              </Link>
            </div>

            {/* Desktop CTA */}
            <div className="hidden shrink-0 items-center gap-2 lg:flex">
              <Link
                to="/auth/login"
                className="rounded-lg px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                data-testid="nav-login-btn"
              >
                Login
              </Link>
              <Link
                to="/get-started"
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-blue-500 hover:shadow-lg"
                data-testid="nav-get-started-btn"
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-2 text-white lg:hidden"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-white/10 bg-[#0F172A] shadow-xl lg:hidden">
            <div className="space-y-2 px-4 py-4">
              <MobileAccordion id="products" label="Products" items={PRODUCTS} />
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className={`block rounded-xl px-3 py-3 text-sm font-semibold ${
                  isActive('/pricing') ? 'bg-white/10 text-blue-400' : 'text-white hover:bg-white/5'
                }`}
                data-testid="mobile-nav-pricing"
              >
                Pricing
              </Link>
              <MobileAccordion id="resources" label="Resources" items={RESOURCES} />
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className={`block rounded-xl px-3 py-3 text-sm font-semibold ${
                  isActive('/contact') ? 'bg-white/10 text-blue-400' : 'text-white hover:bg-white/5'
                }`}
                data-testid="mobile-nav-contact"
              >
                Contact
              </Link>

              {/* Mobile CTA */}
              <div className="mt-2 space-y-2 border-t border-white/10 pt-4">
                <Link
                  to="/auth/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-lg border border-white/20 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-white/10"
                  data-testid="mobile-login-btn"
                >
                  Login
                </Link>
                <Link
                  to="/get-started"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-lg bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-blue-500"
                  data-testid="mobile-get-started-btn"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default LandingNavBar;
